import { useTheme } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { ContainerCard, LineChart } from "../../../../components";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import { useLocalization } from "../../../../hooks/useLocalization";
import { formatDateYMD } from "../../../../shared/utils/date";
import {
  ActivityLineChartDataKey,
  ActivityLineChartDataPoint,
} from "../hooks/useInternalChartData";
import { exportActivityChartCsv } from "../utils/exportCsv";
import { DataAnalyticsChartSkeleton } from "./DataAnalyticsChartSkeleton";

const MIN_Y_AXIS_VALUE = 5;

export interface ActivityLineChartProps {
  data: ActivityLineChartDataPoint[];
  isLoading: boolean;
  isFetching?: boolean;
  isUserLevelDataEnabled?: boolean;
  startDate: Date;
  endDate: Date;
  selectedLicenseNames: string[];
}

export const ActivityLineChart: React.FC<ActivityLineChartProps> = ({
  data,
  isLoading,
  isFetching = false,
  isUserLevelDataEnabled = false,
  startDate,
  endDate,
  selectedLicenseNames,
}) => {
  const theme = useTheme();
  const { t } = useLocalization();
  const isMobile = useIsMobile();

  const [visibleSeries, setVisibleSeries] = useState<Record<string, boolean>>({
    [ActivityLineChartDataKey.HoursInVr]: true,
    [ActivityLineChartDataKey.UniqueActiveUsers]: true,
    [ActivityLineChartDataKey.UserSessions]: true,
  });

  const handleExportCsv = useCallback(() => {
    exportActivityChartCsv(
      data,
      isUserLevelDataEnabled,
      {
        period: t("pages.dataAnalytics.internalUsage.exportColumns.period"),
        periodStart: t(
          "pages.dataAnalytics.internalUsage.exportColumns.periodStart",
        ),
        periodEnd: t(
          "pages.dataAnalytics.internalUsage.exportColumns.periodEnd",
        ),
        hoursInVr: t(
          "pages.dataAnalytics.internalUsage.exportColumns.hoursInVr",
        ),
        userSessions: t(
          "pages.dataAnalytics.internalUsage.exportColumns.userSessions",
        ),
        uniqueActiveUsers: t(
          "pages.dataAnalytics.internalUsage.exportColumns.uniqueActiveUsers",
        ),
      },
      `${t("pages.dataAnalytics.internalUsage.activity.exportFilename")}.csv`,
      { startDate, endDate },
      {
        startDate: t(
          "pages.dataAnalytics.internalUsage.exportColumns.dateRangeStart",
        ),
        endDate: t(
          "pages.dataAnalytics.internalUsage.exportColumns.dateRangeEnd",
        ),
      },
      {
        label: t(
          "pages.dataAnalytics.internalUsage.exportColumns.selectedExperiences",
        ),
        names: selectedLicenseNames,
      },
    );
  }, [
    data,
    isUserLevelDataEnabled,
    t,
    startDate,
    endDate,
    selectedLicenseNames,
  ]);

  const handleToggleSeries = (key: string, checked: boolean) => {
    setVisibleSeries((prev) => ({ ...prev, [key]: checked }));
  };

  const tickLabelByDate = useMemo(
    () =>
      new Map(
        data.map((dp) => [
          dp[ActivityLineChartDataKey.Date],
          dp[ActivityLineChartDataKey.TickLabel],
        ]),
      ),
    [data],
  );

  const xTickFormatter = (value: string) => tickLabelByDate.get(value) ?? value;

  const legendTooltips: Partial<Record<ActivityLineChartDataKey, string>> = {
    [ActivityLineChartDataKey.HoursInVr]: t(
      "pages.dataAnalytics.internalUsage.activity.tooltips.hoursInVr",
    ),
    [ActivityLineChartDataKey.UniqueActiveUsers]: t(
      "pages.dataAnalytics.internalUsage.activity.tooltips.uniqueActiveUsers",
    ),
    [ActivityLineChartDataKey.UserSessions]: t(
      "pages.dataAnalytics.internalUsage.activity.tooltips.userSessions",
    ),
  };

  const series = [
    {
      key: ActivityLineChartDataKey.UserSessions,
      label: t(
        "pages.dataAnalytics.internalUsage.activity.metrics.userSessions",
      ),
      color: theme.colors.metrics.userSessions,
    },
    {
      key: ActivityLineChartDataKey.HoursInVr,
      label: t("pages.dataAnalytics.internalUsage.activity.metrics.hoursInVr"),
      color: theme.colors.metrics.hoursInVr,
    },
    {
      key: ActivityLineChartDataKey.UniqueActiveUsers,
      label: t(
        "pages.dataAnalytics.internalUsage.activity.metrics.uniqueActiveUsers",
      ),
      color: theme.colors.metrics.uniqueActiveUsers,
    },
  ];

  if (isLoading) {
    return (
      <ContainerCard
        exportAction={{
          label: t("pages.dataAnalytics.internalUsage.exportCsv"),
          onClick: handleExportCsv,
        }}
        subtitle={t("pages.dataAnalytics.internalUsage.activity.description")}
        sx={{ height: { xs: 444, md: 544 } }}
        title={t("pages.dataAnalytics.internalUsage.activity.title")}
      >
        <DataAnalyticsChartSkeleton
          chartHeight={isMobile ? 200 : 400}
          showFooterRow
          showTopControls={false}
        />
      </ContainerCard>
    );
  }

  return (
    <ContainerCard
      exportAction={{
        label: t("pages.dataAnalytics.internalUsage.exportCsv"),
        onClick: handleExportCsv,
      }}
      isFetching={isFetching}
      subtitle={t("pages.dataAnalytics.internalUsage.activity.description")}
      sx={{
        height: { xs: 444, md: 544 },
      }}
      title={t("pages.dataAnalytics.internalUsage.activity.title")}
    >
      <LineChart
        data={data}
        legend={{
          tooltips: legendTooltips,
          visibleSeries,
          onToggleSeries: handleToggleSeries,
        }}
        minYAxisValue={isUserLevelDataEnabled ? 0 : MIN_Y_AXIS_VALUE}
        series={series}
        showLegend={true}
        tooltipLabel={(point: ActivityLineChartDataPoint) =>
          t("pages.dataAnalytics.internalUsage.activity.periodRange", {
            start: formatDateYMD(point.periodStart),
            end: formatDateYMD(point.periodEnd),
          })
        }
        xKey={ActivityLineChartDataKey.Date}
        xTickFormatter={xTickFormatter}
      />
    </ContainerCard>
  );
};
